<template>
  <div v-if="!getAppointmentDetails">
    <div
      class="w-full relative border-1 rounded-2xl px-[10px] laptop:px-[20px] pb-[10px] laptop:pb-[25px] largeDesktop:px-[30px] largeDesktop:pb-[35px] pt-[35px] border-gray-300">
      <DetailBackButton fallback="/work-order/appointment" />
      <!-- actions: first 3 as pills, the rest in the ⋮ menu (ActionBar), on the card's top edge like S&R / RQ -->
      <ActionBar v-if="barActions.length" :actions="barActions"
        trigger-class="group w-[40px] h-[40px] rounded-full flex items-center justify-center transition-all duration-300 border-0 bg-transparent p-0"
        class="w-full laptop:px-2.5 absolute -top-[21px] right-0">
        <template #pill="{ action, run, narrow }">
          <button type="button" :disabled="action.disabled"
            class="group flex items-center rounded-full gap-2 py-1.5 pl-3 pr-2 font-roboto font-normal text-[15px] largeDesktop:text-[17px] h-[40px] border-1 whitespace-nowrap transition duration-300 disabled:cursor-not-allowed disabled:opacity-[0.5]"
            :class="narrow ? 'bg-[color:var(--pill)] border-[color:var(--pill)] text-[#fff]' : 'bg-[#fff] border-[color:var(--pill)] text-[color:var(--pill)] enabled:hover:bg-[color:var(--pill)] enabled:hover:text-[#fff]'"
            :style="{ '--pill': action.color }" @click="run(action)">
            {{ action.label }}
            <span
              class="w-[26px] h-[26px] rounded-full flex items-center justify-center bg-white shadow-[0px_0px_15px_-7px_#333]">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M0 0h24v24H0z" fill="none" />
                <path fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  :stroke="action.color" d="M4 12H6.5M14 18L20 12L14 6M20 12H9.5" />
              </svg>
            </span>
          </button>
        </template>
        <template #trigger>
          <img src="../../assets/images/3-dots-icon.png" alt=""
            class="w-full h-full object-contain group-hover:hidden block [@media(max-width:1023px)]:!hidden">
          <img src="../../assets/images/3-dots-icon-hover.png" alt=""
            class="w-full h-full object-contain group-hover:block hidden [@media(max-width:1023px)]:!block">
        </template>
      </ActionBar>
      <div class="w-full items-start flex-col laptop:flex-row flex gap-[20px] largeDesktop:gap-[30px]">
        <div class="w-full flex flex-col gap-[20px] largeDesktop:gap-[30px]">
          <div
            class="flex flex-col items-start w-full shadow-[0px_0px_15px_-2px_#ccc] rounded-[20px] p-[15px] bg-white">
            <!-- details header -->
            <div class="flex justify-between items-center gap-1 pb-[20px] w-full">
              <div class="flex justify-start items-center gap-1">
                <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
                <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
                <!-- Figma "Start Quote" / UQ details: purple "Details" title -->
                <p class="text-[20px] font-semibold font-roboto pl-2 m-0 text-[#9336B4]">
                  Details
                </p>
              </div>
            </div>
            <!-- heading box -->
            <div
              class="flex mobile:flex-col tablet:flex-row justify-between items-center w-full gap-1 !pl-[10px] mobile:p-2.5 tablet:p-[5px] shadow-[0px_0px_15px_-2px_#ccc] rounded-[10px]">
              <h4
                class="mobile:text-center tablet:text-left mobile:w-full tablet:w-auto mobile:border-1 tablet:border-0 border-b border-[#EFEFEF] mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-medium font-roboto m-0 mobile:pb-2.5 tablet:pb-0 text-[#5E366A]">
                {{ $helper.isEmpty(apptDetail.title) ? 'Appointment' : apptDetail.title }}
              </h4>
              <div
                class="flex items-center mobile:justify-between tablet:justify-end mobile:w-full tablet:w-auto gap-2.5 mobile:pt-2.5 tablet:pt-0">
                <h4
                  class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-medium font-roboto m-0 text-[#333333]">
                  Appointment
                  Status:
                </h4>
                <span
                  class="text-[12px] largeDesktop:text-[14px] font-medium font-roboto py-[4px] px-[18px] rounded-[5px] border-1 "
                  :style="{
                    borderColor: getStatusClass(apptDetail.status_text),
                    backgroundColor: `${getStatusClass(apptDetail.status_text)}0D`,
                    color: getStatusClass(apptDetail.status_text)
                  }">{{ $helper.isEmpty(apptDetail.status_text) ? "-" : formatStatus(apptDetail.status_text) }}</span>
              </div>
            </div>
            <!-- details -->
            <div class="w-full grid tablet:grid-cols-3 gap-y-[5px] tablet:gap-y-[17px] mt-[20px]">
              <!-- Figma "Start Quote": address line above the grid (as on the UQ detail) -->
              <div v-if="apptDetail.address" class="tablet:col-span-3">
                <p
                  class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[18px] font-roboto font-normal mb-0 text-[#333333] break-words">
                  {{ apptDetail.address }}
                </p>
              </div>
              <div>
                <span
                  class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-roboto font-normal text-[#EA8A8A]">Appointment
                  ID:</span>
                <p
                  class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-roboto font-normal mb-0 text-[#333333]">
                  {{ $helper.isEmpty(apptDetail.id) ? "-" : apptDetail.id }}
                </p>
              </div>
              <div>
                <span
                  class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-roboto font-normal text-[#EA8A8A]">Quote
                  ID:</span>
                <p
                  class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-roboto font-normal mb-0 text-[#333333]">
                  <!-- no quote comes back as id 0 -->
                  {{ Number(apptDetail.manual_quote?.id) > 0 ? apptDetail.manual_quote.id : "-" }}
                </p>
              </div>
              <div>
                <span
                  class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-roboto font-normal text-[#EA8A8A]">Job
                  Number:</span>
                <p
                  class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-roboto font-normal mb-0 text-[#333333]">
                  {{ $helper.isEmpty(apptDetail.job_number) ? "-" : apptDetail.job_number }}
                </p>
              </div>
              <div>
                <span
                  class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-roboto font-normal text-[#EA8A8A]">Trade
                  Type /
                  Service:</span>
                <p
                  class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-roboto font-normal mb-0 text-[#333333]">
                  {{ apptDetail.trade_id == '0' ? '-' : apptDetail.trade_type + " / " + apptDetail.service_type_text }}
                </p>
              </div>
              <div>
                <span
                  class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-roboto font-normal text-[#EA8A8A]">Scheduled
                  At:</span>
                <p
                  class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-roboto font-normal mb-0 text-[#333333]">
                  {{ convertTimsStampToDate(apptDetail.visit_date) }}
                </p>
              </div>
              <!-- appointments/view has no created_at yet: hidden rather than repeating the visit date (QA D-50, gap G-66) -->
              <div v-if="apptDetail.created_at">
                <span
                  class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-roboto font-normal text-[#EA8A8A]">Created
                  At:</span>
                <p
                  class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-roboto font-normal mb-0 text-[#333333]">
                  {{ convertTimsStampToDate(apptDetail.created_at) }}
                </p>
              </div>
            </div>
            <!-- notes at the bottom of the Details card (was the "View Notes" action) -->
            <AppointmentNotesSection :appointment="apptDetail" />
          </div>
          <!-- Documents section, same as the UQ detail (was the "Documents" action) -->
          <QuoteDocumentsCard v-if="apptDetail?.id" :appointment-id="apptDetail.id" />
        </div>
        <div class="flex flex-col gap-[20px] largeDesktop:gap-[30px] w-full laptop:w-[320px] largeDesktop:w-[350px] laptop:flex-shrink-0">
          <!-- separate Consumer / Partner cards, same as the S&R detail -->
          <ConsumerDetailsCard v-if="apptDetail.consumer" :consumer="apptDetail.consumer"
            :members-to="hasConsumerAccount ? membersRoute : null" />
          <!-- partner appointment: same card as the UQ "Partner Details" (QuoteInfo.vue) — logo, claim text, phone,
               Plan Name, Remarks, Call pill. depp_schedule has no formatted_claim_text / plan_name / remarks (G-81): they come
               from the linked UQ's partner_info; without one the claim text is "{company} Claim ID: {id}" (Android) and Plan Name "-" -->
          <div v-if="partner"
            class="shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] p-[15px] bg-white flex flex-col gap-[10px] font-roboto">
            <div class="flex items-center gap-1 pb-[5px]">
              <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
              <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
              <p class="text-[18px] laptop:text-[20px] font-semibold pl-2 m-0 text-[#333333]">Partner Details</p>
            </div>
            <div class="flex items-center gap-[10px]">
              <img v-if="partner.company_logo" :src="partner.company_logo" :alt="partner.company_name || 'Partner'"
                class="w-[40px] h-[40px] object-contain flex-shrink-0">
              <div class="flex-1 min-w-0">
                <p class="m-0 mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-medium text-[#333333] break-words">
                  {{ partner.formatted_claim_text || `${partner.company_name} Claim ID: ${partner.id}` }}
                </p>
                <a v-if="partner.phone" :href="'tel:' + partner.phone"
                  class="block text-[13px] text-[#666666] no-underline">{{ formatPhone(partner.phone) }}</a>
              </div>
            </div>
            <div class="grid gap-[10px]">
              <div>
                <span class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-normal text-[#EA8A8A]">Plan Name:</span>
                <p class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-normal mb-0 text-[#333333]">
                  {{ partner.plan_name || '-' }}</p>
              </div>
              <div v-if="partner.remarks">
                <span class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-normal text-[#EA8A8A]">Remarks:</span>
                <p class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-normal mb-0 text-[#333333] whitespace-pre-line">
                  {{ partner.remarks }}</p>
              </div>
            </div>
            <!-- Call pill, same as the Consumer Details card -->
            <div v-if="partner.phone" class="flex items-center gap-[10px] pt-[15px] border-t-1 border-[#EFEFEF]">
              <ChatChooser :size="36" :show-chat="false" :phone="partner.phone" :consumer-name="partner.company_name || ''" />
            </div>
          </div>
          <div
            class="laptop:min-w-[320px] largeDesktop:min-w-[350px] w-full laptop:max-w-[320px] largeDesktop:max-w-[350px]">
            <noteBox />
          </div>
          <!-- </div>
      <div class="w-full flex justify-end mt-[20px] largeDesktop:mt-[30px]"> -->
          <MapCard class="laptop:w-[320px] largeDesktop:w-[350px]" :lat="apptDetail.lat" :lng="apptDetail.lng" :address="apptDetail.address || ''" />
        </div>
      </div>
    </div>

    <div id="exampleModal" ref="tradecall" class="modal fade fxModal modal-lg" data-bs-backdrop="static"
      data-bs-keyboard="false" tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
      <div
        class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:px-0 tablet:!w-[450px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
        <TradeCallDialog @event="TradeCallBack" />
      </div>
    </div>

    <div id="serviceType" ref="serviceTypeModal" class="modal fade modal-lg" data-bs-backdrop="static"
      data-bs-keyboard="false" tabindex="-1" aria-labelledby="serviceTypeLabel" aria-hidden="true">
      <div
        class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:px-0 tablet:!w-[450px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
        <ServiceTypeDialog :services="services" @event="serviceTypeCallBack" />
      </div>
    </div>

  </div>
  <!-- status-change confirms: same look as the Sign Out popup -->
  <div id="markAsEnrouteModal" ref="markAsEnrouteModal" class="modal fade fxConfirm" data-bs-backdrop="static"
    tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
    <div
      class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:px-0 tablet:!w-[450px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
      <div class="modal-content">
        <div class="modal-body">
          <ConfirmLayout title="Status Change" button-text="Continue" bs-dismiss @close="closeEnrouteModal()"
            @confirm="continueStatus('enroute')">
            Are you sure you want to mark yourself enroute?
          </ConfirmLayout>
        </div>
      </div>
    </div>
  </div>
  <div id="markAsArrivedModal" ref="markAsArrivedModal" class="modal fade fxConfirm" data-bs-backdrop="static"
    tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
    <div
      class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:px-0 tablet:!w-[450px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
      <div class="modal-content">
        <div class="modal-body">
          <ConfirmLayout title="Status Change" button-text="Continue" bs-dismiss @close="closeArrivedModal()"
            @confirm="continueStatus('arrived')">
            Are you sure you want to mark yourself as Arrived?
          </ConfirmLayout>
        </div>
      </div>
    </div>
  </div>
  <div id="startQuoteModal" ref="startQuoteModal" class="modal fade fxModal fxConfirm" data-bs-backdrop="static"
    tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
    <div
      class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:px-0 tablet:!w-[450px] !absolute tablet:!right-4 !top-0 !m-0 h-full">
      <div class="modal-content">
        <div class="modal-body">
          <ConfirmLayout title="Status Change" button-text="Continue" bs-dismiss @close="closestartQuoteModal()"
            @confirm="continueStatus('startQuote')">
            Are you sure you want to start this quote?
          </ConfirmLayout>
        </div>
      </div>
    </div>
  </div>
  <!-- cancel / disposition / resume / notes / members / documents / create-order page -->
  <AppointmentActionModal ref="actionModal" :appointment="apptDetail || {}" @changed="getAppointmenttDetails"
    @cancelled="$router.push('/work-order/appointment')" @closed="onActionModalClosed" />
</template>

<script>
import DetailBackButton from "@/components/partialComponent/DetailBackButton.vue";
import ConsumerDetailsCard from "@/components/partialComponent/ConsumerDetailsCard.vue";
import MapCard from "@/components/partialComponent/MapCard.vue";
import ActionBar from "@/components/partialComponent/ActionBar.vue";
import ChatChooser from "@/components/chat/ChatChooser.vue";
import { mapActions, mapGetters } from "vuex";
import TradeCallDialog from "@/components/tradecall_dialog/TradeCallDialog.vue";
// import ServiceTypeDialogue from "@/components/ServiceTypeDialogue.vue";
import ServiceTypeDialog from "./ServiceTypeDialog.vue";
import moment from "moment";
import { useToast } from "vue-toastification";
import noteBox from "../notes/apptNoteModal.vue";
import AppointmentNotesSection from "./AppointmentNotesSection.vue";
import QuoteDocumentsCard from "@/components/quote/QuoteDocumentsCard.vue";
const toast = useToast();
import { Modal } from "bootstrap";
import { disableScroll, enableScroll } from "../../helpers/scrollHelper";
import AppointmentActionModal from "./AppointmentActionModal.vue";
import ConfirmLayout from "@/components/confirmDialog/ConfirmLayout.vue";
import { appointmentApi, APPT_STATUS } from "@/services/appointmentApi";
import { rqApi } from "@/services/rqApi";
import { uqApi } from "@/services/uqApi";

// Figma: "Start Quote" goes forward like the other pills, so a right arrow (start-quote.png points up-right).
// White, like the PNG icons; used directly (laptop) and as a CSS mask (hover / mobile).
const ARROW_RIGHT = "data:image/svg+xml," + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="2.5" '
  + 'stroke-linecap="round" stroke-linejoin="round"><path d="M4 12h16M14 6l6 6-6 6"/></svg>',
).replace(/'/g, "%27");

export default {
  components: {
    ConsumerDetailsCard,
    MapCard,
    ActionBar,
    ChatChooser,
    DetailBackButton,
    TradeCallDialog,
    noteBox,
    AppointmentNotesSection,
    QuoteDocumentsCard,
    // ServiceTypeDialogue,
    ServiceTypeDialog,
    AppointmentActionModal,
    ConfirmLayout
  },

  data() {
    return {
      quotePartner: null, // partner_info of the appointment's Universal Quote (G-81)
      isStartQuote: false,
      originlat: 0,
      originlng: 0,
      actionButtons: [],
      serviceId: null,
      // "Service & Repair" picked (HVAC): convert to a booking even when flow_type is not sar
      sarChosen: false,
      // the create-order page was opened; link the new order after it closes
      awaitingOrder: false,

    };
  },
  computed: {
    // Members at this Address is a page now (same one as S&R), not the action modal — a real link (new tab works)
    membersRoute() {
      return { name: "appointment-members", params: { id: this.apptDetail?.id || this.$route.params.id } };
    },
    ...mapGetters([
      "cancelAppointment",
      "getAppointmentDetails",
      "apptDetail",
      "setAppointmentAction",
      "saveApptointmentActionEnroute",
      "saveApptointmentActionArrived",
      "saveApptointmentCreatelObject",
      "services",
      "bookingConverted",
    ]),

    barActions() {
      return this.actionButtons.map(action => ({
        ...action,
        key: action.title,
        label: action.title,
        onClick: () => this.actionButtonClicked(action.title),
      }));
    },

    // partner (NRG / DEPP) appointment: depp_schedule.status = 1 with a company (Android setData)
    partner() {
      const d = this.apptDetail?.depp_schedule;
      if (!d || !this.isTrue(d.status) || this.$helper.isEmpty(d.company_name)) return null;
      // the linked UQ has the claim text / plan / remarks the appointment payload lacks (G-81)
      const q = this.quotePartner || {};
      return { ...d, formatted_claim_text: q.formatted_claim_text, plan_name: q.plan_name, remarks: q.remarks };
    },

    // deleted consumer accounts come back as user_id -1
    hasConsumerAccount() {
      const id = this.apptDetail?.consumer?.user_id;
      return id != null && id !== "" && String(id) !== "-1";
    },
  },

  watch: {
    // partner appointment converted to a UQ: read that quote's partner_info for the Partner Details card
    "apptDetail.manual_quote.id": {
      immediate: true,
      async handler(id) {
        this.quotePartner = null;
        const d = this.apptDetail?.depp_schedule;
        if (!(Number(id) > 0) || !d || !this.isTrue(d.status)) return;
        const res = await uqApi.partnerInfo(id);
        if (res.ok && String(this.apptDetail?.manual_quote?.id) === String(id)) this.quotePartner = res.body?.partner_info || null;
      },
    },
    bookingConverted: function () {
      this.getAppointmenttDetails();
    },

    services: function () {
      disableScroll()
      this.modal = new Modal(this.$refs.serviceTypeModal);
      this.modal.show();
    },

    apptDetail: function () {
      let user = JSON.parse(localStorage.getItem('user'));
      let user_id = user?.user?.user_id;

      if (!user && this.apptDetail?.technician?.user_id != user_id) {
        this.$router.push({
          name: 'NotFound',
        });

      } else {
        this.actionButtons = this.buildActionButtons();
        if (this.linkNewOrder()) return;
        const status = Number(this.apptDetail.status);
        // pending / en route / arrived / cancelled stay here; the rest open their job, quote or order —
        // except when opened from another record's detail ("View Appointment", ?from_detail=1): show this appointment
        const fromDetail = String(this.$route.query.from_detail || "") === "1";
        if (!fromDetail && ![APPT_STATUS.PENDING, APPT_STATUS.EN_ROUTE, APPT_STATUS.ARRIVED, APPT_STATUS.CANCELLED].includes(status)) {
          this.openConvertedJob();
        }
      }
    },

    saveApptointmentActionEnroute: function () {
      toast.success("Appointment has been updated successfully.");
      this.getAppointmenttDetails();
    },

    saveApptointmentActionArrived: function () {
      toast.success("Appointment has been updated successfully.");
      this.getAppointmenttDetails();
    },

    saveApptointmentCreatelObject: function () {
      this.getAppointmenttDetails();
    },
    cancelAppointment: function () {
      toast.success("Appointment has been updated successfully.");
      this.getAppointmenttDetails();
    },
  },

  mounted() {
    this.getAppointmenttDetails();
  },

  methods: {
    ...mapActions([
      "appointmentCancel",
      "getAppointmentDetailsAPICall",
      "markAsEnrouteAPICall",
      "markAsArrivedAPICall",
      "appointmentCreate",
      "setCommponent",
      "getServices",
      "convretToBooking",
      "apptServiceUpdate"
    ]),

    // Converted appointments open their job/quote. Disposed, cancelled, no-sale ones have neither
    // (booking missing, manual_quote.id = 0) and stay here instead of redirecting to /quote/detail/0.
    openConvertedJob() {
      const query = { is_from_mobile: Number(this.$route.query.is_from_mobile) === 1 ? 1 : 0 };
      const booking = this.apptDetail.booking;
      const quoteId = this.apptDetail.manual_quote?.id;
      const orderId = Number(this.apptDetail.order?.id);
      const status = Number(this.apptDetail.status);
      const hasBooking = booking?.id > 0;
      const hasQuote = quoteId > 0;
      const openBooking = hasBooking && (this.apptDetail.flow_type == "sar" || !hasQuote);
      // converted to a Replacement Quote (or closed with only an order on it)
      const openOrder = orderId > 0 && (status === APPT_STATUS.CONVERTED_ORDER
        || (status === APPT_STATUS.CLOSED && !hasBooking && !hasQuote));

      // replace: going Back must not land here again and redirect in a loop
      if (openOrder) {
        this.$router.replace({ path: `/replacement-quote/detail/${orderId}`, query });
      } else if (openBooking) {
        if (booking.status + "" == "4") {
          this.serviceUpdateCall(booking.id, "sar")
          this.$router.replace({ path: `/select/unit/${booking.id}`, query });
        } else {
          this.$router.replace({ path: `/job/detail/${booking.id}`, query });
        }
      } else if (hasQuote) {
        if (this.isStartQuote) {
          this.serviceUpdateCall(quoteId, "iq")
          this.$router.replace({ path: `/quote/detail/${quoteId}`, query: { is_edit: 1 } });
        } else {
          this.$router.replace({ path: `/quote/detail/${quoteId}` });
        }
      }
    },
    getStatusClass(status) {
      const statusClasses = {
        'DISPOSED': '#686D76',
        'EN ROUTE': '#0EA5E9',
        'PENDING': '#F59E0B',
        'CANCELLED': '#B91C1C',
        'NO SALE': '#CA6180',
        'ARRIVED': '#14B8A6',
        'CONVERTED TO BOOKING': '#5992B2',
        'CLOSED': '#374151',
      };
      return statusClasses[status] || '#6B7280'; // used as an inline colour, not a class
    },
    formatStatus(status) {
      return status
        ?.toLowerCase()
        .replace(/\b\w/g, char => char.toUpperCase());
    },
    getNotesTxt(note) {

      return note.replace("\n", "<br>")
    },
    getAppointmenttDetails() {
      this.getAppointmentDetailsAPICall({
        id: this.$route.params.id,
      });
    },
    closeArrivedModal() {
      enableScroll()
      this.modal.hide();
    },
    closeEnrouteModal() {
      enableScroll()
      this.modal.hide();
    },
    continueStatus(item) {
      if (item == 'arrived') {
        this.closeArrivedModal();
        this.markAsArrivedAPICall({
          appointment_id: this.$route.params.id,
        });
      }
      else if (item == 'enroute') {
        this.closeEnrouteModal();
        this.markAsEnrouteAPICall({
          appointment_id: this.$route.params.id,
        });
      }
      else if (item == 'startQuote') {
        this.closestartQuoteModal();
        if (this.apptDetail.flow_type == "sar" || this.sarChosen) {
          this.convertToBooking();
        } else {
          this.startQuote();
        }
      }

    },
    markAsStatus(item) {
      disableScroll()
      if (item == 'arrived') {
        this.modal = new Modal(this.$refs.markAsArrivedModal);
      }
      else if (item == 'enroute') {
        this.modal = new Modal(this.$refs.markAsEnrouteModal);
      }
      this.modal.show();
    },

    TradeCallBack(data, waiveTradeCall) {
      enableScroll()
      if (data == "close") {
        this.modal.hide();
        document.documentElement.style.overflow = ''; // Reset HTML element
        document.documentElement.style.paddingRight = '0px';

      } else if (data == "confirm") {
        this.modal.hide();
        document.documentElement.style.overflow = 'hidden'; // Target HTML element
        if (window.innerWidth >= 992) {
          document.documentElement.style.paddingRight = '15px';
        } else {
          document.documentElement.style.paddingRight = '0px';
        }
        this.isStartQuote = true;
        let params = {
          appointment_id: this.$route.params.id,
        }

        if (waiveTradeCall == 1) {
          params['waive_trade_call_fee'] = 1
        }
        else {
          params['waive_trade_call_fee'] = 0
        }

        this.appointmentCreate(params);
      }
    },

    startQuote() {
      this.isStartQuote = true;
      console.log(this.$route.params.id)
      this.appointmentCreate({
        appointment_id: this.$route.params.id,
      });
    },

    convertTimsStampToDate(val) {
      return moment.unix(val).format("MM/DD/YYYY - hh:mm A");
    },

    convertNotesTimsStampToDate(val) {
      return moment.unix(val).format("hh:mm a MM/DD/YYYY");
    },

    actionButtonClicked(action) {
      const a = this.apptDetail;
      if (action == "Start Quote") this.sarChosen = false;
      switch (action) {
        case "Claim Appointment": return this.claimAppointment();
        case "Complete": return this.completeAppointment();
        case "Dispositioned Leads": return this.$refs.actionModal.openDispose();
        case "Resume": return this.$refs.actionModal.openResume();
        case "Cancel Appointment": return this.$refs.actionModal.openCancel();
        case "Order Details": return a.order?.id && this.$router.push(`/replacement-quote/detail/${a.order.id}`);
        case "Booking Details": return a.booking?.id && this.$router.push(`/job/detail/${a.booking.id}`);
        case "Quote Summary": return a.order_summary_url
          ? this.$refs.actionModal.openFrame("Quote Summary", a.order_summary_url)
          : toast.error("Quote summary is not available.");
        case "Replacement Quote": return this.startOrderFlow(false);
        case "Finalize Quote": return this.startOrderFlow(true);
        case "Service & Repair": return this.serviceAndRepair();
      }
      if (action == "On My Way") {
        this.markAsStatus('enroute');
      } else if (action == "Mark As Arrived") {
        this.markAsStatus('arrived');
      } else if (action == "Start Quote") {
        if (this.apptDetail.flow_type == "uq" && this.apptDetail.depp_schedule?.is_tradecall_fee_applicable == 1) {
          disableScroll()
          this.modal = new Modal(this.$refs.tradecall);
          this.modal.show();
        } else {
          if (this.apptDetail.depp_schedule?.can_service_type_update == '1') {
            this.getServices();
          } else {
            this.startQuoteCall()
          }
        }
      }
    },

    // Android AppointmentDetailFragment.setData / setOrderButtons. Status actions first: the first
    // 3 entries are the pills (ActionBar).
    buildActionButtons() {
      const a = this.apptDetail || {};
      const status = Number(a.status);
      const type = String(a.type || "").toLowerCase();
      const noCharge = type.includes("no_charge");
      const unfinishedRq = type.includes("unfinished_replacement_quote");
      const isPartner = this.isTrue(a.depp_schedule?.status);
      const hasOrder = Number(a.order?.id) > 0;
      const hasBooking = Number(a.booking?.id) > 0;
      const list = [];
      const add = (title, image, color) => list.push({ title, image, color });
      const orderOrBooking = () => {
        if (hasOrder) add("Order Details", "view-order.png", "#5992B2");
        else if (hasBooking) add("Booking Details", "view-order.png", "#5992B2");
      };

      // partner (NRG) appointments must be claimed first; someone else's claimed one is notes-only
      if (isPartner) {
        if (!this.isTrue(a.is_claimed)) {
          add("Claim Appointment", "approved-quote.png", "#1FA971");
          return list;
        }
        // someone else's claimed appointment: no actions (its notes show under the Details card)
        if (String(a.technician?.user_id ?? "") !== String(this.currentUserId() ?? "")) return list;
      }

      if (status === APPT_STATUS.PENDING) {
        add("On My Way", "on-my-way.png", "#FC819E");
        if (noCharge) add("Complete", "complete-job.png", "#1FA971");
        else if (unfinishedRq) add("Quote Summary", "view-quote.png", "#7695FF");
      } else if (status === APPT_STATUS.EN_ROUTE) {
        add("Mark As Arrived", "mark-as-arrived.png", "#168EFF");
        if (unfinishedRq) add("Quote Summary", "view-quote.png", "#7695FF");
      } else if (status === APPT_STATUS.ARRIVED) {
        if (noCharge) {
          add("Complete", "complete-job.png", "#1FA971");
          orderOrBooking();
        } else if (unfinishedRq) {
          add("Finalize Quote", "resubmit-quote.png", "#A084E8");
          add("Quote Summary", "view-quote.png", "#7695FF");
        } else if (!isPartner && String(a.trade_type || "").toLowerCase() === "hvac") {
          // Android offers Service & Repair / Replacement Quote here; Start Quote stays for Universal Quotes
          add("Service & Repair", "start-evaluation.png", "#00B7C2");
          add("Replacement Quote", "edit-quote.png", "#A084E8");
          add("Start Quote", ARROW_RIGHT, "#F67280");
        } else {
          add("Start Quote", ARROW_RIGHT, "#F67280");
        }
      } else if (status === APPT_STATUS.CONVERTED_ORDER) {
        if (hasOrder) add("Order Details", "view-order.png", "#5992B2");
      } else if (status === APPT_STATUS.CLOSED) {
        if (!noCharge) orderOrBooking();
      } else if (status === APPT_STATUS.DISPOSED) {
        orderOrBooking();
        if (unfinishedRq) add("Quote Summary", "view-quote.png", "#7695FF");
      }

      if (this.isTrue(a.can_show_dispose)) add("Dispositioned Leads", "cancel-quote.png", "#686D76");
      if (this.isTrue(a.can_show_resume)) add("Resume", "resume-job.png", "#14B8A6");
      if (this.isTrue(a.can_cancel)) add("Cancel Appointment", "cancel-quote.png", "#D7385E");
      return list;
    },

    // last 10 digits through the (xxx) xxx-xxxx mask, same as QuoteInfo (UQ Partner Details)
    formatPhone(value) {
      const d = String(value || "").replace(/\D/g, "").slice(-10);
      if (d.length <= 3) return d;
      return `(${d.slice(0, 3)}) ${d.slice(3, 6)}` + (d.length > 6 ? `-${d.slice(6)}` : "");
    },
    isTrue(v) {
      return v === true || v === 1 || v === "1" || String(v).toLowerCase() === "true";
    },

    currentUserId() {
      try {
        return JSON.parse(localStorage.getItem("user"))?.user?.user_id;
      } catch (e) {
        return null;
      }
    },

    claimAppointment() {
      this.$dialog.show({
        title: "Claim Appointment",
        text: "Are you sure you want to claim this Appointment?",
        buttonText: "Claim Now",
        onConfirm: async () => {
          const res = await appointmentApi.claim(this.apptDetail.id);
          if (!res.ok) return toast.error(res.message || "Unable to claim this appointment.");
          toast.success(res.message || "Appointment has been claimed.");
          this.getAppointmenttDetails();
        },
      });
    },

    // no-charge appointments
    completeAppointment() {
      this.$dialog.show({
        title: "Alert",
        text: "Are you sure you want to complete this Appointment?",
        buttonText: "Yes",
        onConfirm: async () => {
          const res = await appointmentApi.close(this.apptDetail.id);
          if (!res.ok) return toast.error(res.message || "Unable to complete this appointment.");
          toast.success(res.message || "Appointment has been completed successfully");
          this.getAppointmenttDetails();
        },
      });
    },

    // Android ChooseServicesFragment: pick the service, then convert to a booking
    serviceAndRepair() {
      const a = this.apptDetail;
      this.sarChosen = true;
      this.serviceId = null;
      if (this.isTrue(a.depp_schedule?.is_allied) && String(a.service_id) === "9") {
        this.convertToBooking();
      } else {
        this.getServices();
      }
    },

    // "Replacement Quote" / "Finalize Quote": one active job at a time, then the Create Replacement Quote flow (/rq/*)
    async startOrderFlow(finalize) {
      const id = this.apptDetail.id;
      const guard = await appointmentApi.singleActiveRecord(id);
      if (!guard.ok) return this.showActiveRecordError(guard);
      const open = async () => {
        const res = await rqApi.allowCreate(id);
        if (!res.ok || !res.body?.is_allow) {
          return toast.error(res.message || "Oops! Something Went Wrong");
        }
        // native Create Replacement Quote flow: the appointment's consumer is looked up by phone, step 2 is prefilled from the appointment
        const phone = this.apptDetail.consumer?.phone;
        this.$router.push({ path: "/rq/consumer", query: { appointment_id: id, ...(phone ? { phone } : {}) } });
      };
      if (finalize) {
        this.$dialog.show({ title: "Alert", text: "Are you sure you want to Finalize Quote?", buttonText: "Yes", onConfirm: open });
      } else {
        open();
      }
    },

    showActiveRecordError(res) {
      const guard = res.activeRecord;
      const record = guard?.active_record;
      if (!record?.id) return toast.error(res.message || "Something went wrong.");
      this.$dialog.show({
        title: "Dispositioned Leads",
        text: guard.message || res.message,
        buttonText: record.button_text || "Resolve",
        onConfirm: () => {
          if (record.type === "appointment") this.$router.push(`/appointment/detail/${record.id}`);
          else if (record.type === "booking") this.$router.push(`/job/detail/${record.id}`);
          else this.$router.push(`/replacement-quote/detail/${record.id}`);
        },
      });
    },

    // The order page is cross-origin, so its success URL (order_id=…) cannot be read here. Reload: if the
    // backend linked the order, the watcher redirects to it; linkNewOrder covers an order not yet converted.
    onActionModalClosed(type) {
      if (type === "frame" && this.awaitingOrder) this.getAppointmenttDetails();
    },

    // Android calls appointments/convert/order after the order page succeeds
    linkNewOrder() {
      if (!this.awaitingOrder) return false;
      this.awaitingOrder = false;
      const a = this.apptDetail;
      const orderId = Number(a.order?.id);
      const status = Number(a.status);
      if (!(orderId > 0) || [APPT_STATUS.CONVERTED_ORDER, APPT_STATUS.CLOSED].includes(status)) return false;
      appointmentApi.convertToOrder(a.id, orderId).then((res) => {
        if (res.ok) toast.success("The order has been placed successfully.");
        else toast.error(res.message || "Unable to link the order to this appointment.");
        this.getAppointmenttDetails();
      });
      return true;
    },


    startQuoteCall() {
      disableScroll()
      this.modal = new Modal(this.$refs.startQuoteModal);
      this.modal.show();
    },
    closestartQuoteModal() {
      enableScroll()
      this.modal.hide();
    },

    convertToBooking() {
      this.convretToBooking({
        appointment_id: this.$route.params.id,
        // Service & Repair sends the service picked in the dialog (Android ChooseServicesFragment)
        service_id: this.sarChosen && this.serviceId ? this.serviceId : this.apptDetail.service_id,
      });
    },

    serviceTypeCallBack(data, check) {
      this.serviceId = check
      enableScroll()
      if (data == "close") {
        this.sarChosen = false;
        this.modal.hide();
      } else if (data == "confirm") {
        this.modal.hide();
        this.startQuoteCall()
      }
    },

    serviceUpdateCall(quote_id, quote_type) {
      if (this.apptDetail.depp_schedule?.can_service_type_update == '1') {
        this.apptServiceUpdate({
          "quote_id": quote_id,
          "quote_type": quote_type,
          "service_id": this.serviceId
        })
      }
    }
  },
};
</script>
